import { getLocale } from '@borealis/aurora';
import type { DavsEnumAttribute, DavsTaxonomyAttribute } from '@finn-no/mobility-item-insertion-shared';
import { i18n } from '@lingui/core';
import { Trans } from '@lingui/react/macro';
import '@warp-ds/components/icon';
import { Button, Modal, Switch, Toggle } from '@warp-ds/react';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { EquipmentWidgetModel } from '../../../types/widget.js';
import { ExpandableSection } from '../expandable-section.js';

interface EquipmentWidgetProps extends Omit<EquipmentWidgetModel, 'type'> {
  attribute: DavsTaxonomyAttribute | DavsEnumAttribute;
}

export const EquipmentWidget = (props: EquipmentWidgetProps) => {
  const { control, getValues, setValue } = useFormContext();
  const [equipment, setEquipment] = useState<Array<number>>([]);
  const [editMode, setEditMode] = useState(false);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const [activeButtonRef, setActiveButtonRef] = useState<React.RefObject<HTMLButtonElement | null> | null>(null);

  const headerType = props.headerType;
  const values = props.attribute?.values || [];
  const topLevel = values.filter((v) => !('parentId' in v));
  const categories = topLevel.filter((v) => values.some((other) => 'parentId' in other && other.parentId === v.id));
  // 'persistable' is only present on taxonomy values, treat missing as true
  const other = topLevel.filter((v) => ('persistable' in v ? v.persistable : true));

  const numberOfSelectedItems = props.attribute?.values.filter(
    (value) => ('persistable' in value ? value.persistable : true) && equipment.includes(value.id),
  ).length;

  const handleEquipmentChange = (selectedEquipment: number, isChecked: boolean) => {
    const newEquipment = isChecked
      ? [...equipment, selectedEquipment]
      : equipment.filter((item) => item !== selectedEquipment);

    setEquipment(newEquipment);
    setValue('equipment', newEquipment, { shouldDirty: true });
  };

  useEffect(() => {
    // TODO: The values we currently get are the text values, not the ids (this should work with both however)
    const chosenEquipment: Array<string | number> = getValues().equipment ?? [];

    // Remove potential duplicate values
    const uniqueEquipment = chosenEquipment.filter((item, index) => chosenEquipment.indexOf(item) === index);

    // Get the DAVS ids of the chosen equipment (in case we are back to using id's, check that as well)
    const chosenEquipmentIds = props.attribute?.values
      .filter(
        (value) =>
          uniqueEquipment.includes(value.text) ||
          uniqueEquipment.includes(`${value.id}`) ||
          uniqueEquipment.includes(value.id),
      )
      .map((value) => value.id);

    setEquipment(chosenEquipmentIds);
  }, []);

  const handleCloseModal = () => {
    setEditMode(false);
    // Defer focus restoration until Modal closes and releases focus trap
    setTimeout(() => {
      activeButtonRef?.current?.focus();
    }, 50); // 50ms delay ensures Modal cleanup completes
  };

  const header = (
    <header className="flex justify-between items-center mb-16">
      <h2 className={headerType === 'section-title' ? 't3' : 'mb-0'}>
        <Trans id="mobility-item-insertion.form.header.equipment_header">Equipment</Trans>
      </h2>

      {numberOfSelectedItems > 0 && (
        <div>
          <Button
            ref={editButtonRef}
            link
            quiet
            className="flex items-center gap-8"
            onClick={() => {
              setActiveButtonRef(editButtonRef);
              setEditMode(true);
            }}
            data-testid="add-equipment"
          >
            <w-icon name="Edit" size="18px" className="s-icon-primary" locale={getLocale()} />
            <Trans
              id="mobility-item-insertion.form.equipment.button.edit"
              comment="Text on button for enabling editing equipment"
            >
              Edit equipment
            </Trans>
          </Button>
        </div>
      )}
    </header>
  );

  const noEquipmentSelected = (
    <div className="flex flex-col items-center w-full md:w-2/5">
      <w-icon name="Checklist" size="large" className="mb-8" locale={getLocale()} />
      <div className="mb-24">
        <Trans
          id="mobility-item-insertion.form.equipment.none_selected"
          comment="Text shown when no equipment is selected. It's optional to select equipment."
        >
          No equipment selected (optional)
        </Trans>
      </div>
      <Button
        ref={addButtonRef}
        fullWidth
        className="flex items-center gap-8"
        onClick={() => {
          setActiveButtonRef(addButtonRef);
          setEditMode(true);
        }}
        data-testid="add-equipment"
      >
        <Trans
          id="mobility-item-insertion.form.equipment.button.add"
          comment="Text on button for adding equipment when no equipment is selected"
        >
          Add equipment
        </Trans>
      </Button>
    </div>
  );

  const numberOfOtherSelectedItems = other.filter((value) => equipment.includes(value.id)).length;
  const showOtherEquipment = showSelectedOnly ? numberOfOtherSelectedItems > 0 : other.length > 0;

  const otherEquipmentInModal = showOtherEquipment ? (
    <fieldset key="other-equipment">
      <div className="flex items-baseline">
        <legend className="text-s font-bold mb-4">
          <Trans
            id="mobility-item-insertion.form.equipment.other.title"
            comment="Title for the 'other' category of equipment that does not belong to a group"
          >
            Other
          </Trans>
        </legend>
      </div>
      <div className="grid sm:grid-cols-1 md:grid-cols-3 gap-x-32">
        {other
          .filter((value) => (showSelectedOnly ? equipment.includes(value.id) : true))
          .map((value) => (
            <div key={value.id} data-testid={value.id}>
              <Toggle
                label={value.text}
                type="checkbox"
                checked={equipment.includes(value.id)}
                onChange={(checked: boolean) => {
                  handleEquipmentChange(value?.id, checked);
                }}
              />
            </div>
          ))}
      </div>
    </fieldset>
  ) : null;

  const isAnyWithinCategorySelected = (categoryId: number) => {
    return props.attribute?.values.some(
      (value) => 'parentId' in value && categoryId === value.parentId && equipment.includes(value.id),
    );
  };

  const editModal = (
    <Modal
      open={editMode}
      title={i18n.t({
        id: 'mobility-item-insertion.form.header.equipment_header',
        message: 'Equipment',
      })}
      right
      onDismiss={handleCloseModal}
      // @ts-ignore: Custom style for modal width
      style={{ '--w-modal-width': '768px' }}
    >
      <div className="flex gap-8 pl-0 sticky top-0 left-0 s-bg pt-4 pb-24 z-30">
        <Switch value={showSelectedOnly} onClick={() => setShowSelectedOnly(!showSelectedOnly)} />
        <div>
          <Trans
            id="mobility-item-insertion.form.equipment.show_selected_only"
            comment="Text for toggle to show only selected equipment items in the modal"
          >
            Show only selected items ({numberOfSelectedItems})
          </Trans>
        </div>
      </div>
      <div
        ref={(node) => {
          if (node?.parentElement) {
            node.parentElement.style.scrollPaddingTop = '60px';
            node.parentElement.style.scrollPaddingBottom = '76px';
          }
        }}
        className="grid gap-24"
        data-attribute={props.name}
      >
        {categories.length > 0 ? (
          categories
            ?.filter((category) => (showSelectedOnly ? isAnyWithinCategorySelected(category.id) : true))
            ?.map((category) => (
              <fieldset key={category.id}>
                <div className="flex items-baseline">
                  <legend className="text-s font-bold mb-4">{category.text}</legend>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-32">
                  {props.attribute?.values
                    .filter((value) => (showSelectedOnly ? equipment.includes(value.id) : true))
                    .filter((value) => 'parentId' in value && value.parentId === category.id)
                    .map((value) => (
                      <div key={value.id} data-testid={value.id}>
                        <Toggle
                          label={value.text}
                          type="checkbox"
                          checked={equipment.includes(value.id)}
                          onChange={(checked: boolean) => {
                            handleEquipmentChange(value?.id, checked);
                          }}
                        />
                      </div>
                    ))}
                </div>
              </fieldset>
            ))
            .concat(otherEquipmentInModal ? [otherEquipmentInModal] : [])
        ) : (
          <div className="grid sm:grid-cols-1 md:grid-cols-3">
            {props.attribute?.values
              .filter((value) => (showSelectedOnly ? equipment.includes(value.id) : true))
              .map((value) => (
                <div key={value.id} data-testid={value.id}>
                  <Toggle
                    label={value.text}
                    type="checkbox"
                    checked={equipment.includes(value.id)}
                    onChange={(checked: boolean) => {
                      handleEquipmentChange(value?.id, checked);
                    }}
                  />
                </div>
              ))}
          </div>
        )}
        {/* Hidden field to store the equipment value */}
        <input type="hidden" {...control.register('equipment')} />
      </div>
      <div className="flex justify-end pt-24 sticky bottom-0 right-0 s-bg">
        <Button primary onClick={handleCloseModal} data-testid="close-equipment">
          <Trans
            id="mobility-item-insertion.form.equipment.modal.button.done"
            comment="Text on button for closing the equipment modal"
          >
            Done
          </Trans>
        </Button>
      </div>
    </Modal>
  );

  const selectedEquipment = (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
      {props.attribute?.values
        .filter((value) => ('persistable' in value ? value.persistable : true))
        .filter((attrValue) => equipment.includes(attrValue.id))
        .map((value) => (
          <div key={value.id} data-testid={`selected-equipment-${value.id}`} className="min-w-0 break-words">
            {value.text}
          </div>
        ))}
    </div>
  );

  return (
    <>
      {header}
      {numberOfSelectedItems === 0 ? noEquipmentSelected : null}
      {editMode && editModal}
      {numberOfSelectedItems > 0 && !editMode ? (
        <ExpandableSection
          body={selectedEquipment}
          readMoreText={i18n.t({
            id: 'mobility-item-insertion.form.equipment.readmore',
            message: 'Show entire equipment list ({length})',
            values: { length: numberOfSelectedItems },
            comment:
              'Button text to show full equipment list of expandable section where {length} is the number of selected items and should be placed in parentheses',
          })}
        />
      ) : (
        ''
      )}
    </>
  );
};
